import React, { useState, useEffect, useContext } from 'react'
import { useTrans } from 'hooks'
import { PaginaNode } from '@gql/graphql'
import { Breadcrumbs, Button, WContainer } from '@components/atoms'
import {
  RicercaNewsCard,
  RicercaPaginaCard,
  RicercaProdottoCard,
  RicercaVideoTutorialCard,
} from '@components/molecules'
import { Page, ProdottiCorrelati } from 'components/organisms'
import styles from './SearchTemplate.module.sass'
import { Swiper, SwiperSlide } from 'swiper/react'
import { A11y } from 'swiper/modules'
import { CartContext } from '@utils/cartContext'
// import { useRecommendation } from '@hooks/useRecommendation' // TODO: RECOMMENDATION

interface Props {
  pagina?: PaginaNode
  risultati?: {
    tipologia?: string
    is_accessorio?: boolean
    is_abbigliamento?: boolean
    object?: any
  }[]
  query?: String
}

const ITEMS_PER_LOAD = 6

const SearchTemplate = (props: Props) => {
  const { pagina = undefined, risultati = undefined, query = undefined } = props
  const t = useTrans()
  const { cart } = useContext(CartContext)

  const emptyItem = {
    name: t('Sembra che non ci siano risultati per la tua ricerca'),
    description: t('description_empty_search_results'),
  }

  const listings = [
    {
      pk: 1,
      type: 'prodotto',
      name: t('Prodotti'),
      items: risultati?.filter(
        (x) => x.tipologia === 'prodotto' && !x.is_accessorio && !x.is_abbigliamento
      ),
    },
    {
      pk: 2,
      type: 'accessori',
      name: t('Accessori'),
      items: risultati?.filter((x) => x.tipologia === 'prodotto' && x.is_accessorio),
    },
    {
      pk: 3,
      type: 'abbigliamento',
      name: t('Abbigliamento'),
      items: risultati?.filter((x) => x.tipologia === 'prodotto' && x.is_abbigliamento),
    },
    {
      pk: 4,
      type: 'articoli',
      name: t('Articoli'),
      items: risultati?.filter((x) => x.tipologia === 'news'),
    },
    {
      pk: 5,
      type: 'video',
      name: t('Video'),
      items: risultati?.filter((x) => x.tipologia === 'video'),
    },
    {
      pk: 6,
      type: 'altro',
      name: t('Altre pagine'),
      items: risultati?.filter((x) => x.tipologia === 'pagina' || x.tipologia === 'faq'),
    },
  ].filter((x) => !!x.items?.length && x.items?.length > 0)

  const [selectedListing, setSelectedListing] = useState(
    listings?.length > 0 ? listings[0].type : null
  )
  const [itemsToShow, setItemsToShow] = useState(ITEMS_PER_LOAD)

  const data = listings.find((x) => x.type === selectedListing)?.items || []

  useEffect(() => {
    setItemsToShow(ITEMS_PER_LOAD)
    // TODO handleProductImpressionTracking(data.slice(0, ITEMS_PER_LOAD), cart)
  }, [selectedListing])

  const handleShowMore = () => {
    setItemsToShow((prev) => prev + ITEMS_PER_LOAD)
  }

  // const { recommendedForYou } = useRecommendation(null) TODO: RECOMMENDATION

  const renderResultsByType = (item) => {
    switch (item.tipologia) {
      case 'prodotto':
      case 'accessori':
      case 'abbigliamento':
        return (
          <RicercaProdottoCard
            prodotto={{
              pk: parseInt(item?.django_id),
              nome: item?.titolo,
              url: item?.url,
              previewImage: item?.image,
              categoria: {
                alias: item?.categoria,
                boxTitolo: item?.categoria,
              },
              utilizzo: item?.utilizzo_alias,
              sottotitolo: item?.sottotitolo,
              disponibile: item?.disponibile,
            }}
          />
        )
      case 'news':
        return (
          <RicercaNewsCard
            news={{
              pk: item.pk,
              titolo: item.content_auto,
              sottotitolo: item.sottotitolo || item.descrizione,
              url: item.url,
              previewImage: item.image,
              categoria: {
                titolo: item?.categoria,
              },
            }}
          />
        )

      case 'video':
        return (
          <RicercaVideoTutorialCard
            videoTutorial={{
              pk: item.pk,
              titolo: item.content_auto,
              url: item.url,
              categoria: {
                titolo: item?.categoria,
              },
              idYoutube: item?.id_youtube,
            }}
          />
        )

      case 'faq':
      case 'pagina':
        return (
          <RicercaPaginaCard
            pagina={{
              pk: item.pk,
              titolo: item.content_auto,
              descrizione: item.descrizione,
              url: item.url,
            }}
          />
        )
    }
  }

  return (
    <Page pagina={pagina} className={`${styles.root}`}>
      <main>
        <div className={styles.head}>
          <WContainer>
            <div className={styles.headLayout}>
              <Breadcrumbs breadcrumbs={pagina.breadcrumbs} showLast />
              <h2 className={styles.title}>
                {t('Risultati di ricerca per')} “{query}”
              </h2>
            </div>
          </WContainer>
        </div>
        {listings.length ? (
          <div className={styles.layout}>
            <div className={styles.tabs}>
              <WContainer>
                <Swiper slidesPerView={'auto'} modules={[A11y]}>
                  {listings.map((tab) => (
                    <SwiperSlide>
                      <button
                        key={tab.pk}
                        type="button"
                        onClick={() => {
                          setSelectedListing(tab.type)
                        }}
                        disabled={tab.type == selectedListing}
                        id={`tab-${tab.pk}`}
                        role="tab"
                        aria-selected={tab.type == selectedListing ? 'true' : 'false'}
                        aria-controls={`tabpanel-${tab.pk}`}
                        className={styles.tab}
                      >
                        <h2 className={styles.tabTitle}>{`${tab.name} (${tab.items?.length})`}</h2>
                      </button>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </WContainer>
            </div>
            <WContainer>
              <React.Fragment key={listings.find((x) => x.type === selectedListing)?.pk}>
                <div className={styles.results}>
                  {data.slice(0, itemsToShow).map((risultato) => (
                    <div key={risultato?.object?.id}>{renderResultsByType(risultato)}</div>
                  ))}
                </div>
              </React.Fragment>
              {itemsToShow < data.length && (
                <div className={styles.loadMoreContainer}>
                  <Button
                    className={styles.loadMoreButton}
                    onClick={handleShowMore}
                    variant="secondary"
                    label={t(
                      Math.min(ITEMS_PER_LOAD, data.length - itemsToShow) === 1
                        ? 'Cerca {0} altro risultato'
                        : 'Cerca altri {0} risultati',
                      [Math.min(ITEMS_PER_LOAD, data.length - itemsToShow).toString()]
                    )}
                  />
                </div>
              )}
            </WContainer>
          </div>
        ) : (
          <div className={styles.emptyLayout}>
            <WContainer>
              <div className={styles.empty}>
                {!!emptyItem?.name && <h2 className={styles.emptyTitle}>{emptyItem.name}</h2>}
                {!!emptyItem?.description && (
                  <div
                    className={styles.emptyDescription}
                    dangerouslySetInnerHTML={{ __html: emptyItem.description }}
                  />
                )}
              </div>
            </WContainer>
            {/* <ProdottiCorrelati prodotti={recommendedForYou} className={styles.correlati} /> TODO: RECOMMENDATION */}
          </div>
        )}
      </main>
    </Page>
  )
}

export default SearchTemplate
